import { useState, useEffect } from "react";
import { SiteNavigation } from "../components/navigation/SiteNavigation";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import PrivacyScoreBadge from "../components/PrivacyScoreBadge";
import FeatureShowcase from "../components/FeatureShowcase";
import { 
  Music, 
  Video, 
  Calendar, 
  ShoppingBag, 
  Users, 
  Play, 
  ArrowRight,
  Sparkles,
  Ticket,
  Star,
  BarChart3,
  Heart,
  DollarSign,
  Flame,
  User,
  Disc3,
  Film,
  ShieldCheck,
  Coins,
  ReceiptText,
  Clock,
  Send
} from "lucide-react";
import { Button } from "../components/ui/button";

const LandingPage = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [trendingData, setTrendingData] = useState({ artists: [], events: [], content: [] });
  const [featured, setFeatured] = useState({ artists: [], music: [], videos: [], products: [], date: "" });
  const [loading, setLoading] = useState(true);
  const [featuredTab, setFeaturedTab] = useState("music");
  const [openFAQ, setOpenFAQ] = useState(-1);

  const faqs = [
    {
      q: "How much does StardomFans cost?",
      a: "Creating your profile and setting up your stage is 100% free. We only make money when you make money — a 12% platform fee on your earnings (10% for Founders Circle artists). Stripe processing + payout fees are netted at wire time so our cut stays honest. No hidden monthly subscriptions, ever."
    },
    {
      q: "What types of content can I offer my fans?",
      a: "You have total control. Charge monthly subscriptions for exclusive content, accept direct tips, sell pay-per-view digital files (unreleased music, videos, artwork), or offer personalized supporter experiences and meet & greets."
    },
    {
      q: "How do I get paid, and how fast?",
      a: "Sales convert Tokens to USD 1:1. Refunds available days 1-7, funds locked days 8-10. On your chosen cadence (Day-11 default / biweekly / monthly), the AI treasury fires a Stripe Connect transfer straight to your bank. StardomFans keeps 12% (10% for Founders Circle) and Stripe processing + payout fees are netted from your share at wire time."
    },
    {
      q: "Do I keep 100% ownership of my content and data?",
      a: "Absolutely. You own your intellectual property, your brand, and your content. StardomFans is simply the tool that helps you monetize it. Export your data or update your page anytime."
    },
    {
      q: "How is StardomFans different from Linktree, Patreon, or OnlyFans?",
      a: "We are in no competition with any other platforms or entertainment companies. We respect all other platforms and entertainment companies. All are welcome to StardomFans.com."
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [trendingRes, featuredRes] = await Promise.all([
          axios.get(`${API}/discover/trending`),
          axios.get(`${API}/featured/today`)
        ]);
        setTrendingData(trendingRes.data);
        setFeatured(featuredRes.data);
      } catch (e) {
        console.error("Failed to fetch data:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate("/dashboard");
    } else {
      navigate("/auth");
    }
  };

  const handleExplore = () => {
    navigate("/fandom");
  };

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong public-navigation-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans Ltd." className="w-14 h-14 object-contain" />
          </Link>
          
          <SiteNavigation scope="home" />

          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                <Button 
                  onClick={() => navigate("/fan-dashboard")}
                  className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] font-semibold rounded-full px-6"
                  data-testid="fan-dashboard-btn"
                >
                  My Dashboard
                </Button>
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="bg-[#0A0E18] text-white border border-[#1A2030] hover:bg-[#1A2030] font-semibold rounded-full px-5"
                  data-testid="artist-dashboard-btn"
                >
                  Artist Studio
                </Button>
              </div>
            ) : (
              <Button 
                onClick={handleGetStarted}
                className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] font-semibold rounded-full px-6"
                data-testid="get-started-btn"
              >
                Get Started
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-60"
          style={{ backgroundImage: `url(https://customer-assets.emergentagent.com/job_creator-vault-54/artifacts/8nkhg6wh_OnBzn.jpg)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#000000]/50 via-transparent to-[#000000]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A0E18] border border-[#1A2030]">
                <Star className="w-4 h-4 text-[#EC008C]" />
                <span className="text-sm text-[#8A92A0]">Artists keep 88–90% of revenue</span>
              </div>
              
              <h1 className="font-['Unbounded'] text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-white leading-[1.1]">
                Own Your Fanbase.<br />
                <span className="text-[#EC008C] text-glow-primary">Keep Your Earnings.</span>
              </h1>
              
              <p className="text-lg text-[#8A92A0] max-w-xl leading-relaxed">
                Build your free StardomFans profile, share exclusive content, and connect deeply with your community — without the heavy platform fees.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={handleGetStarted}
                  className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] font-semibold rounded-full px-8 py-6 text-lg neon-primary"
                  data-testid="hero-get-started-btn"
                >
                  Create Your Free Stage
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  onClick={handleExplore}
                  variant="outline"
                  className="border-[#1A2030] text-white hover:bg-[#0A0E18] hover:text-[#EC008C] hover:border-[#EC008C] rounded-full px-8 py-6 text-lg"
                  data-testid="hero-explore-btn"
                >
                  See Live Examples
                </Button>
              </div>
              <p className="text-xs text-[#6A7280] flex items-center gap-2 mt-2" data-testid="hero-risk-reverser">
                <Sparkles className="w-3.5 h-3.5 text-[#EC008C]" />
                No credit card required. Setup takes 2 minutes.
              </p>

              {/* Privacy Score — creator-first differentiator */}
              <PrivacyScoreBadge variant="hero" className="mt-6 max-w-md" />
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 animate-fade-in-up stagger-2">
              <div className="card p-6 col-span-2">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-[#EC008C]/10 flex items-center justify-center">
                    <Music className="w-7 h-7 text-[#EC008C]" />
                  </div>
                  <div>
                    <p className="text-3xl font-['Unbounded'] font-bold text-white">Music & Video</p>
                    <p className="text-[#8A92A0]">Upload and stream your content</p>
                  </div>
                </div>
              </div>
              <div className="card p-6">
                <div className="w-12 h-12 rounded-xl bg-[#FF6BC5]/10 flex items-center justify-center mb-4">
                  <Ticket className="w-6 h-6 text-[#FF6BC5]" />
                </div>
                <p className="text-2xl font-['Unbounded'] font-bold text-white">Sell Tickets</p>
                <p className="text-sm text-[#8A92A0]">Direct to supporters</p>
              </div>
              <div className="card p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D14A85]/10 flex items-center justify-center mb-4">
                  <ShoppingBag className="w-6 h-6 text-[#D14A85]" />
                </div>
                <p className="text-2xl font-['Unbounded'] font-bold text-white">Merch Store</p>
                <p className="text-sm text-[#8A92A0]">Built-in commerce</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Showcase — animated end-to-end walkthroughs */}
      <FeatureShowcase />

      {/* Artist + Buyer Journey — 5 Steps (KYC + Tokens + Tax + Refund Window + Payout) */}
      <section id="artist-journey" className="py-24 px-6 border-t border-[#1A2030]" data-testid="artist-journey-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-[#EC008C]/10 border border-[#EC008C]/30 text-[#EC008C] text-xs font-semibold uppercase tracking-widest mb-4">
              How It Works
            </span>
            <h2 className="font-['Unbounded'] text-4xl sm:text-5xl font-bold text-white mb-4">
              The <span className="text-[#EC008C]">5-Step Secure Journey</span>
            </h2>
            <p className="text-[#8A92A0] text-lg max-w-3xl mx-auto">
              KYC for tax &amp; legal compliance protects both sellers and buyers. Tokens replace card checkouts — top-up is fee-free and tax-free. Sales tax is applied at product checkout (when Tokens are spent) and routed to the correct country / province / state / city in our in-house tax bucket. Refund window ends day 7 · payout to artist on day 11 (or biweekly/monthly if the artist chose that cadence).
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 relative">
            {/* Step 1 — Seller KYC */}
            <div className="card p-6 relative overflow-hidden group hover:border-[#EC008C]/40 transition-colors" data-testid="journey-step-1">
              <div className="absolute -top-4 -right-4 text-[120px] font-black text-[#EC008C]/5 leading-none pointer-events-none select-none">1</div>
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-[#EC008C]/15 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-6 h-6 text-[#EC008C]" />
                </div>
                <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold mb-2">Seller</p>
                <h3 className="font-['Unbounded'] text-base font-bold text-white mb-3">ID-Verify &amp; Upload</h3>
                <p className="text-[#8A92A0] leading-relaxed text-sm">
                  Sellers pass a KYC check, then upload digital products to their own store. No verification, no listing — protects fans from fraud.
                </p>
              </div>
            </div>

            {/* Step 2 — Buyer KYC + fee-free Token Top-Up */}
            <div className="card p-6 relative overflow-hidden group hover:border-[#EC008C]/40 transition-colors" data-testid="journey-step-2">
              <div className="absolute -top-4 -right-4 text-[120px] font-black text-[#EC008C]/5 leading-none pointer-events-none select-none">2</div>
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-[#EC008C]/15 flex items-center justify-center mb-4">
                  <Coins className="w-6 h-6 text-[#EC008C]" />
                </div>
                <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold mb-2">Buyer</p>
                <h3 className="font-['Unbounded'] text-base font-bold text-white mb-3">Buy Tokens</h3>
                <p className="text-[#8A92A0] leading-relaxed text-sm">
                  Fans complete KYC and top up their wallet with Tokens (1 Token = $1 USD) — <strong className="text-white">zero fees</strong> on top-up. Sales tax isn't charged here; it's added at product checkout when Tokens are spent, then routed to the correct country / province / state / city bucket.
                </p>
              </div>
            </div>

            {/* Step 3 — 48h Fraud Hold */}
            <div className="card p-6 relative overflow-hidden group hover:border-[#EC008C]/40 transition-colors" data-testid="journey-step-3">
              <div className="absolute -top-4 -right-4 text-[120px] font-black text-[#EC008C]/5 leading-none pointer-events-none select-none">3</div>
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-[#FF6BC5]/15 flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-[#FF6BC5]" />
                </div>
                <p className="text-[10px] uppercase tracking-widest text-[#FF6BC5] font-bold mb-2">Security</p>
                <h3 className="font-['Unbounded'] text-base font-bold text-white mb-3">48-Hour Hold</h3>
                <p className="text-[#8A92A0] leading-relaxed text-sm">
                  Every buyer&apos;s first Token top-up is held for 48 hours so we can flag fraudulent cards. Once both buyer and seller are bona-fide, every subsequent purchase clears instantly.
                </p>
              </div>
            </div>

            {/* Step 4 — Spend Tokens + Refund Window */}
            <div className="card p-6 relative overflow-hidden group hover:border-[#EC008C]/40 transition-colors" data-testid="journey-step-4">
              <div className="absolute -top-4 -right-4 text-[120px] font-black text-[#EC008C]/5 leading-none pointer-events-none select-none">4</div>
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-[#EBB84C]/15 flex items-center justify-center mb-4">
                  <ReceiptText className="w-6 h-6 text-[#EBB84C]" />
                </div>
                <p className="text-[10px] uppercase tracking-widest text-[#EBB84C] font-bold mb-2">Sale</p>
                <h3 className="font-['Unbounded'] text-base font-bold text-white mb-3">Spend &amp; Hold</h3>
                <p className="text-[#8A92A0] leading-relaxed text-sm">
                  Fans buy tracks, videos, tickets and 1:1s with Tokens. Tokens convert to USD 1:1. Refunds available days 1-7, locked days 8-10 for dispute review.
                </p>
              </div>
            </div>

            {/* Step 5 — Payout Day 11 */}
            <div className="card p-6 relative overflow-hidden group hover:border-[#EC008C]/40 transition-colors" data-testid="journey-step-5">
              <div className="absolute -top-4 -right-4 text-[120px] font-black text-[#EC008C]/5 leading-none pointer-events-none select-none">5</div>
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-[#8DB87A]/15 flex items-center justify-center mb-4">
                  <Send className="w-6 h-6 text-[#8DB87A]" />
                </div>
                <p className="text-[10px] uppercase tracking-widest text-[#8DB87A] font-bold mb-2">Payout</p>
                <h3 className="font-['Unbounded'] text-base font-bold text-white mb-3">Day 11 Payout</h3>
                <p className="text-[#8A92A0] leading-relaxed text-sm">
                  On your chosen cadence (Day-11 / biweekly / monthly), a real Stripe Connect transfer fires to the artist's bank — <strong className="text-white">88% Standard</strong> (or <strong className="text-white">90% Founders Circle</strong>) minus Stripe processing + payout fees. Every dollar and every tax row is signed to the ledger for audit-grade compliance.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-14">
            <Button
              onClick={handleGetStarted}
              className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] font-semibold rounded-full px-8 py-6 text-lg"
              data-testid="journey-cta-btn"
            >
              Get Started for Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="font-['Unbounded'] text-4xl sm:text-5xl font-bold text-white mb-4">
              Everything You Need
            </h2>
            <p className="text-[#8A92A0] text-lg max-w-2xl mx-auto">
              One platform to manage your entire career. No more juggling multiple apps.
            </p>
          </div>

          <div className="bento-grid">
            {/* Large Feature Card */}
            <div onClick={handleGetStarted} className="col-span-12 md:col-span-8 card p-8 relative overflow-hidden group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-[#EC008C]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-[#EC008C]/10 flex items-center justify-center mb-6">
                  <Music className="w-8 h-8 text-[#EC008C]" />
                </div>
                <h3 className="font-['Unbounded'] text-2xl font-bold text-white mb-3">
                  Music & Video Hosting
                </h3>
                <p className="text-[#8A92A0] text-lg leading-relaxed max-w-xl">
                  Upload unlimited music and videos. Stream directly to your supporters. 
                  No algorithms deciding who sees your content.
                </p>
              </div>
            </div>

            {/* Small Cards */}
            <div onClick={handleGetStarted} className="col-span-12 md:col-span-4 card p-8 cursor-pointer hover:border-[#FF6BC5]/30 transition-colors">
              <div className="w-14 h-14 rounded-xl bg-[#FF6BC5]/10 flex items-center justify-center mb-6">
                <Calendar className="w-7 h-7 text-[#FF6BC5]" />
              </div>
              <h3 className="font-['Unbounded'] text-xl font-bold text-white mb-2">
                Event Management
              </h3>
              <p className="text-[#8A92A0]">
                Create events, manage tickets, track sales.
              </p>
            </div>

            <div onClick={handleGetStarted} className="col-span-12 md:col-span-6 card p-8 cursor-pointer hover:border-[#D14A85]/30 transition-colors">
              <div className="w-14 h-14 rounded-xl bg-[#D14A85]/10 flex items-center justify-center mb-6">
                <ShoppingBag className="w-7 h-7 text-[#D14A85]" />
              </div>
              <h3 className="font-['Unbounded'] text-xl font-bold text-white mb-2">
                Built-in Store
              </h3>
              <p className="text-[#8A92A0]">
                Sell merch, albums, and digital content directly from your profile.
              </p>
            </div>

            <div onClick={handleGetStarted} className="col-span-12 md:col-span-6 card p-8 cursor-pointer hover:border-[#EC008C]/30 transition-colors">
              <div className="w-14 h-14 rounded-xl bg-[#EC008C]/10 flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-[#EC008C]" />
              </div>
              <h3 className="font-['Unbounded'] text-xl font-bold text-white mb-2">
                Supporter Connection
              </h3>
              <p className="text-[#8A92A0]">
                Build direct relationships with your audience. No middleman.
              </p>
            </div>

            {/* NEW: Supporter Subscriptions */}
            <div onClick={handleGetStarted} className="col-span-12 md:col-span-6 card p-8 relative overflow-hidden group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-[#D14A85]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-xl bg-[#D14A85]/10 flex items-center justify-center mb-6">
                  <Heart className="w-7 h-7 text-[#D14A85]" />
                </div>
                <h3 className="font-['Unbounded'] text-xl font-bold text-white mb-2">
                  Supporter Subscriptions
                </h3>
                <p className="text-[#8A92A0]">
                  Let fans support you monthly like Patreon. Create tiers with exclusive perks.
                </p>
              </div>
            </div>

            {/* NEW: Analytics Dashboard */}
            <div onClick={handleGetStarted} className="col-span-12 md:col-span-6 card p-8 relative overflow-hidden group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-[#8DB87A]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-xl bg-[#8DB87A]/10 flex items-center justify-center mb-6">
                  <BarChart3 className="w-7 h-7 text-[#8DB87A]" />
                </div>
                <h3 className="font-['Unbounded'] text-xl font-bold text-white mb-2">
                  Analytics Dashboard
                </h3>
                <p className="text-[#8A92A0]">
                  Track earnings, plays, subscriber growth. Know exactly how your career is growing.
                </p>
              </div>
            </div>
          </div>

          {/* Revenue Highlight */}
          <div className="mt-12 card p-8 bg-gradient-to-r from-[#8DB87A]/10 to-[#EC008C]/10 border-[#8DB87A]/20">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-[#8DB87A]/20 flex items-center justify-center">
                  <DollarSign className="w-8 h-8 text-[#8DB87A]" />
                </div>
                <div>
                  <h3 className="font-['Unbounded'] text-2xl font-bold text-white mb-1">
                    Keep Up To 90% of Your Revenue
                  </h3>
                  <p className="text-[#8A92A0]">
                    88/12 Standard split · 90/10 for Founders Circle. Stripe fees netted at wire time — never at top-up.
                  </p>
                </div>
              </div>
              <Button 
                onClick={handleGetStarted}
                className="bg-[#8DB87A] text-[#000000] hover:bg-[#4ADE80] font-semibold rounded-full px-8"
              >
                Start Earning
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Community Showcase */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div onClick={handleGetStarted} className="relative rounded-2xl overflow-hidden aspect-[4/3] group cursor-pointer">
              <img 
                src="https://customer-assets.emergentagent.com/job_creator-vault-54/artifacts/wfd4btoh_Futuristic%20fan%20event%20with%20glowing%20lights%20%281%29.png"
                alt="Artists meeting fans at StardomFans event"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#000000]/80 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-['Unbounded'] text-xl font-bold text-white">Meet Your Supporters</p>
                <p className="text-[#8A92A0] text-sm mt-1">Build real connections at live events</p>
              </div>
            </div>
            <div onClick={handleGetStarted} className="relative rounded-2xl overflow-hidden aspect-[4/3] group cursor-pointer">
              <img 
                src="https://customer-assets.emergentagent.com/job_creator-vault-54/artifacts/bghlce55_Futuristic%20concert%20selfie%20with%20fans.png"
                alt="Futuristic concert selfie with fans"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#000000]/80 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-['Unbounded'] text-xl font-bold text-white">Create Moments</p>
                <p className="text-[#8A92A0] text-sm mt-1">The future of fandom starts here</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Today Section */}
      {!loading && (featured.artists.length > 0 || featured.music.length > 0 || featured.videos.length > 0 || featured.products.length > 0) && (
        <section className="py-24 px-6 relative" data-testid="featured-today-section">
          <div className="absolute inset-0 bg-gradient-to-b from-[#000000] via-[#0A0A12] to-[#000000]" />
          <div className="relative z-10 max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D14A85]/20 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-[#D14A85]" />
                </div>
                <div>
                  <h2 className="font-['Unbounded'] text-3xl font-bold text-white">
                    Featured Today
                  </h2>
                  <p className="text-[#6A7280] text-sm mt-0.5">Fresh picks daily &middot; {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                </div>
              </div>
              <div className="flex gap-1 p-1 rounded-full bg-[#0A0E18] border border-[#1A2030]">
                {[
                  { key: "music", label: "New Music", icon: Disc3 },
                  { key: "videos", label: "New Videos", icon: Play },
                  { key: "artists", label: "Artists", icon: User },
                  { key: "products", label: "Products", icon: ShoppingBag }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setFeaturedTab(tab.key)}
                    data-testid={`featured-tab-${tab.key}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      featuredTab === tab.key 
                        ? 'bg-[#EC008C] text-[#000000]' 
                        : 'text-[#8A92A0] hover:text-white'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Music Tab */}
            {featuredTab === "music" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="featured-music-grid">
                {featured.music.length > 0 ? featured.music.map((track) => (
                  <Link
                    key={track.content_id}
                    to={`/artist/${track.artist_id}`}
                    className="group card p-4 flex gap-4 items-center hover:border-[#EC008C]/30 transition-all"
                    data-testid={`featured-music-${track.content_id}`}
                  >
                    <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-[#EC008C]/20 to-[#FF6BC5]/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {track.thumbnail ? (
                        <img src={track.thumbnail} alt={track.title} className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <Music className="w-7 h-7 text-[#EC008C]" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-semibold truncate group-hover:text-[#EC008C] transition-colors">{track.title}</p>
                      <p className="text-[#6A7280] text-sm truncate">{track.artist_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Play className="w-3 h-3 text-[#8A92A0]" />
                        <span className="text-xs text-[#8A92A0]">{(track.plays || 0).toLocaleString()} plays</span>
                      </div>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-[#EC008C]/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <Play className="w-4 h-4 text-[#EC008C]" />
                    </div>
                  </Link>
                )) : (
                  <div className="col-span-full text-center py-12">
                    <Music className="w-10 h-10 text-[#27272A] mx-auto mb-3" />
                    <p className="text-[#6A7280]">No music uploaded yet. Be the first!</p>
                  </div>
                )}
              </div>
            )}

            {/* Videos Tab */}
            {featuredTab === "videos" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="featured-videos-grid">
                {featured.videos.length > 0 ? featured.videos.map((video) => (
                  <Link
                    key={video.content_id}
                    to={`/artist/${video.artist_id}`}
                    className="group card overflow-hidden hover:border-[#D14A85]/30 transition-all"
                    data-testid={`featured-video-${video.content_id}`}
                  >
                    <div className="aspect-video bg-gradient-to-br from-[#D14A85]/20 to-[#A855F7]/20 flex items-center justify-center relative">
                      {video.thumbnail ? (
                        <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
                      ) : (
                        <Film className="w-10 h-10 text-[#D14A85]" />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-[#D14A85] flex items-center justify-center">
                          <Play className="w-6 h-6 text-white ml-0.5" />
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-white font-semibold truncate group-hover:text-[#D14A85] transition-colors">{video.title}</p>
                      <p className="text-[#6A7280] text-sm truncate">{video.artist_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Play className="w-3 h-3 text-[#8A92A0]" />
                        <span className="text-xs text-[#8A92A0]">{(video.plays || 0).toLocaleString()} views</span>
                      </div>
                    </div>
                  </Link>
                )) : (
                  <div className="col-span-full text-center py-12">
                    <Film className="w-10 h-10 text-[#27272A] mx-auto mb-3" />
                    <p className="text-[#6A7280]">No videos uploaded yet. Be the first!</p>
                  </div>
                )}
              </div>
            )}

            {/* Artists Tab */}
            {featuredTab === "artists" && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5" data-testid="featured-artists-grid">
                {featured.artists.length > 0 ? featured.artists.map((artist) => (
                  <Link
                    key={artist.artist_id}
                    to={`/artist/${artist.artist_id}`}
                    className="group card p-5 text-center hover:border-[#FF6BC5]/30 transition-all"
                    data-testid={`featured-artist-${artist.artist_id}`}
                  >
                    <div className="w-20 h-20 rounded-full mx-auto mb-3 overflow-hidden bg-gradient-to-br from-[#FF6BC5]/20 to-[#D14A85]/20 flex items-center justify-center ring-2 ring-transparent group-hover:ring-[#FF6BC5]/50 transition-all">
                      {artist.profile_image ? (
                        <img src={artist.profile_image} alt={artist.stage_name} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-8 h-8 text-[#FF6BC5]" />
                      )}
                    </div>
                    <p className="text-white font-semibold text-sm truncate group-hover:text-[#FF6BC5] transition-colors">{artist.stage_name}</p>
                    <p className="text-[#6A7280] text-xs mt-1 truncate">{artist.genre || "Artist"}</p>
                  </Link>
                )) : (
                  <div className="col-span-full text-center py-12">
                    <Users className="w-10 h-10 text-[#27272A] mx-auto mb-3" />
                    <p className="text-[#6A7280]">No artists yet. Create your profile!</p>
                  </div>
                )}
              </div>
            )}

            {/* Products Tab */}
            {featuredTab === "products" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="featured-products-grid">
                {featured.products.length > 0 ? featured.products.map((product) => (
                  <Link
                    key={product.product_id}
                    to={`/products/${product.product_id}`}
                    className="group card p-4 hover:border-[#D14A85]/30 transition-all"
                    data-testid={`featured-product-${product.product_id}`}
                  >
                    <div className="aspect-[4/3] rounded-lg bg-gradient-to-br from-[#D14A85]/10 to-[#EC008C]/10 mb-3 overflow-hidden flex items-center justify-center">
                      {product.images && product.images.length > 0 ? (
                        <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <ShoppingBag className="w-10 h-10 text-[#D14A85]/50" />
                      )}
                    </div>
                    <p className="text-white font-semibold truncate group-hover:text-[#D14A85] transition-colors">{product.name}</p>
                    <p className="text-[#6A7280] text-xs mt-1 truncate">by {product.artist_name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[#EC008C] font-bold">${product.price}</span>
                      <span className="text-xs text-[#8A92A0] capitalize">{product.category}</span>
                    </div>
                  </Link>
                )) : (
                  <div className="col-span-full text-center py-12">
                    <ShoppingBag className="w-10 h-10 text-[#27272A] mx-auto mb-3" />
                    <p className="text-[#6A7280]">No products yet. Start selling!</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Trending Section */}
      {!loading && trendingData.events.length > 0 && (
        <section className="py-24 px-6 bg-[#0A0E18]/50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <h2 className="font-['Unbounded'] text-3xl font-bold text-white">
                Upcoming Events
              </h2>
              <Link 
                to="/fandom" 
                className="text-[#EC008C] hover:text-[#FF3399] font-medium flex items-center gap-2"
                data-testid="view-all-events-link"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trendingData.events.slice(0, 3).map((event) => (
                <Link 
                  key={event.event_id} 
                  to={`/events/${event.event_id}`}
                  className="event-card card p-6 group"
                  data-testid={`event-card-${event.event_id}`}
                >
                  <div className="aspect-video rounded-lg bg-[#1A2030] mb-4 overflow-hidden">
                    {event.cover_image ? (
                      <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover" />
                    ) : (
                      <img src="https://customer-assets.emergentagent.com/job_creator-vault-54/artifacts/8xs65yky_Sold-out%20concert%20frenzy%20under%20vibrant%20lights.png" alt={event.title} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <h3 className="font-['Unbounded'] text-lg font-semibold text-white mb-2 group-hover:text-[#EC008C] transition-colors">
                    {event.title}
                  </h3>
                  <p className="text-[#8A92A0] text-sm mb-3">{event.venue} • {event.location}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-[#EC008C] font-semibold">${event.ticket_price}</span>
                    <span className="text-sm text-[#8A92A0]">{event.event_date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-6 border-t border-[#1A2030]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-[#EC008C]/10 border border-[#EC008C]/30 text-[#EC008C] text-xs font-semibold uppercase tracking-widest mb-4">
              FAQ
            </span>
            <h2 className="font-['Unbounded'] text-4xl sm:text-5xl font-bold text-white mb-4">
              Answers for <span className="text-[#EC008C]">Creators</span>
            </h2>
            <p className="text-[#8A92A0] text-lg">
              Everything you need to know before you build your stage.
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((item, i) => {
              const isOpen = openFAQ === i;
              return (
                <div
                  key={i}
                  className={`card overflow-hidden transition-colors ${isOpen ? "border-[#EC008C]/40" : ""}`}
                  data-testid={`faq-item-${i}`}
                >
                  <button
                    onClick={() => setOpenFAQ(isOpen ? -1 : i)}
                    className="w-full text-left flex items-center justify-between gap-4 p-6 hover:bg-[#0A0E18]/50 transition-colors"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-['Unbounded'] text-base sm:text-lg font-semibold text-white">
                      {item.q}
                    </span>
                    <span className={`w-8 h-8 rounded-full bg-[#EC008C]/10 flex items-center justify-center flex-shrink-0 transition-transform ${isOpen ? "rotate-45" : ""}`}>
                      <span className="text-[#EC008C] text-xl leading-none">+</span>
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-6 pt-0" data-testid={`faq-answer-${i}`}>
                      <p className="text-[#8A92A0] leading-relaxed">{item.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="artists" className="py-24 px-6 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(https://customer-assets.emergentagent.com/job_creator-vault-54/artifacts/ne9fd6gc_K-pop%20idols%20shine%20with%20fans%E2%80%99%20love.png)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#000000] via-[#000000]/90 to-[#000000]" />
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="font-['Unbounded'] text-4xl sm:text-5xl font-bold text-white mb-6" data-testid="final-cta-heading">
            Your Stage is Waiting. Ready to Meet Your Superfans?
          </h2>
          <p className="text-[#8A92A0] text-lg mb-8 max-w-2xl mx-auto">
            Stop leaving your income in the hands of social media algorithms. Join StardomFans today and start building a sustainable creative career on your own terms.
          </p>
          <Button 
            onClick={handleGetStarted}
            className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] font-semibold rounded-full px-10 py-6 text-lg neon-primary"
            data-testid="cta-get-started-btn"
          >
            Launch Your Free Profile
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-xs text-[#8A92A0] mt-5" data-testid="final-cta-reassurance">
            Takes less than 2 minutes to set up · Cancel anytime · 100% secure
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-24 px-6 bg-[#050810] border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 mb-16">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{mixBlendMode:"lighten"}} />
              </div>
              <p className="text-[#8A92A0] text-sm">
                Where artists own their stage.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Platform</h4>
              <ul className="space-y-3">
                <li><Link to="/awards/2027" className="text-[#EC008C] hover:text-[#FF3399] text-sm font-semibold" data-testid="top100-footer-link">🏆 2027 Top 100 Awards</Link></li>
                <li><Link to="/learn" className="text-[#8A92A0] hover:text-[#EC008C] text-sm" data-testid="teaching-center-link">Teaching Center</Link></li>
                <li><Link to="/fandom" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Fandom</Link></li>
                <li><a href="#features" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Features</a></li>
                <li><a href="#artists" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">For Artists</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-3">
                <li><Link to="/terms" className="text-[#8A92A0] hover:text-[#EC008C] text-sm" data-testid="terms-link">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-[#8A92A0] hover:text-[#EC008C] text-sm" data-testid="privacy-link">Privacy Policy</Link></li>
                <li><Link to="/copyright" className="text-[#8A92A0] hover:text-[#EC008C] text-sm" data-testid="copyright-link">Copyright Policy</Link></li>
                <li><Link to="/security" className="text-[#8A92A0] hover:text-[#EC008C] text-sm" data-testid="security-link">Security &amp; Trust</Link></li>
                <li><Link to="/privacy-report" className="text-[#00E68A] hover:text-[#00E68A] text-sm font-semibold" data-testid="privacy-report-link">Privacy Report</Link></li>
                <li><Link to="/status" className="text-[#8A92A0] hover:text-[#EC008C] text-sm" data-testid="status-link">Platform Status</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Support</h4>
              <ul className="space-y-3">
                <li><button onClick={() => { const btn = document.querySelector('[data-testid="support-chat-toggle"]'); if(btn) btn.click(); }} className="text-[#8A92A0] hover:text-[#EC008C] text-sm cursor-pointer">Contact</button></li>
                <li><Link to="/partnerships" className="text-[#8A92A0] hover:text-[#EC008C] text-sm" data-testid="partnerships-link">Partnerships</Link></li>
                <li><a href="#" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Help Center</a></li>
              </ul>
            </div>
          </div>

          <div className="watermark font-['Unbounded'] text-center select-none">
            StardomFans Ltd.
          </div>

          <div className="border-t border-[#1A2030] pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[#8A92A0] text-sm">
              © {new Date().getFullYear()} StardomFans Ltd. All rights reserved. · Operates stardomfans.com.
            </p>
            <p className="text-[#8A92A0] text-sm">
              Welcome home to the StardomFans Family.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
